import { type ReactNode } from "react";
import { StatusBadge, type StatusLevel } from "../../components/index.ts";
import type { AgentStatus, IntentReviewStatus } from "./types.ts";

/**
 * Small presentational helpers shared by the AgentDNA screens, built on the control plane's own
 * components (StatusBadge) and design tokens — no third-party chart/animation libraries. Ported in
 * spirit (not pixels) from the AgentDNA dashboard's MetricTile / ThreatPill / review pill.
 */

/** A row of headline metric tiles. */
export function MetricTiles(props: {
  readonly tiles: ReadonlyArray<{ readonly label: string; readonly value: ReactNode; readonly hint?: string }>;
}): ReactNode {
  return (
    <div className="adna-metrics" role="group" aria-label="Headline metrics">
      {props.tiles.map((t) => (
        <div key={t.label} className="adna-metric">
          <span className="adna-metric__label">{t.label}</span>
          <span className="adna-metric__value">{t.value}</span>
          {t.hint !== undefined && <span className="adna-metric__hint">{t.hint}</span>}
        </div>
      ))}
    </div>
  );
}

/** Map an AgentDNA traffic-light status to the control plane's StatusBadge level. */
export function statusLevel(status: AgentStatus): StatusLevel {
  switch (status) {
    case "safe":
      return "healthy";
    case "warn":
      return "warn";
    case "threat":
      return "error";
  }
}

/** A threat indicator pill: a flagged exchange reads as an error, a clean one as healthy. */
export function ThreatPill(props: { readonly threat: boolean }): ReactNode {
  return props.threat ? (
    <StatusBadge level="error" label="Threat" size="sm" />
  ) : (
    <StatusBadge level="healthy" label="Safe" size="sm" />
  );
}

/** The intent human-review state pill (Ongoing / Acknowledged / Flagged). */
export function ReviewPill(props: { readonly status: IntentReviewStatus }): ReactNode {
  const level: StatusLevel =
    props.status === "Flagged" ? "error" : props.status === "Acknowledged" ? "healthy" : "neutral";
  return <StatusBadge level={level} label={props.status} size="sm" />;
}
